import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { Column } from 'features/Yucollect/utils/types';
import { call, put, takeLatest } from 'redux-saga/effects';
import { setCurrentCampaign } from 'redux/slice/crm/CRM.slice';
import {
  DispositionSummaryResponse,
  TASK_TYPES,
  TaskType,
  fetchBorrowersFailure,
  fetchBorrowersRequest,
  fetchBorrowersSuccess,
  fetchCampaignsByAgent,
  fetchCampaignsByAgentFailure,
  fetchCampaignsByAgentSuccess,
  fetchDispositionSummaryFailure,
  fetchDispositionSummaryRequest,
  fetchDispositionSummarySuccess,
  saveCampaign,
  saveCampaignFailure,
  saveCampaignSuccess,
  searchBorrowersFailure,
  searchBorrowersRequest,
  searchBorrowersSuccess,
} from 'redux/slice/supervisor-dashboard/SupervisorDashboard.slice';
import {
  IFetchCampaignsByAgentRequest,
  IFetchCampaignsByAgentResponse,
  ISaveCampaignRequest,
  ISaveCampaignResponse,
} from 'redux/types/CRM.types';
import { BorrowersResponse } from 'redux/types/SupervisorDashboard.types';
import { crmEndPoints, supervisorCRMEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

interface FetchBorrowersPayload {
  agentId: string;
  campaignId: string;
  limit: number;
  offset: number;
  taskType: TaskType;
  dispositionIds: string[];
  untouched?: boolean;
  columns?: Column[];
}

interface SearchBorrowersPayload {
  agentId: string;
  campaignId: string;
  limit: number;
  offset: number;
  taskType: TaskType;
  searchValue: string;
  dispositionIds: string[];
}

function* fetchBorrowersSaga(
  action: PayloadAction<FetchBorrowersPayload>
): Generator<any, void, any> {
  try {
    const {
      agentId,
      campaignId,
      limit,
      offset,
      taskType,
      dispositionIds,
      untouched,
      columns,
    } = action.payload;

    const response: AxiosResponse<BorrowersResponse> = yield call(
      HTTPClient.PostRequest,
      supervisorCRMEndPoints.FETCH_BORROWERS_IN_CURRENT_CAMPAIGN(
        agentId,
        campaignId,
        limit,
        offset,
        taskType
      ),
      {
        filters: { columns: columns ?? [] },
        dispositionIds,
        followUp: false,
        startHour: 0,
        endHour: 23,
        ...(untouched !== undefined && { untouched }),
      }
    );

    yield put(fetchBorrowersSuccess(response.data));
  } catch (error: any) {
    const payload = {
      error: {
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Failed to fetch campaigns',
        details: error?.response?.data?.details,
      },
    };
    yield put(fetchBorrowersFailure(payload));
  }
}

function* searchBorrowersSaga(
  action: PayloadAction<SearchBorrowersPayload>
): Generator<any, void, any> {
  try {
    const {
      agentId,
      campaignId,
      limit,
      offset,
      taskType,
      searchValue,
      dispositionIds,
    } = action.payload;

    const response: AxiosResponse<BorrowersResponse> = yield call(
      HTTPClient.PostRequest,
      supervisorCRMEndPoints.FETCH_SEARCHED_BORROWERS_IN_CURRENT_CAMPAIGN(
        agentId,
        campaignId,
        limit,
        offset,
        searchValue,
        taskType
      ),
      {
        filters: { columns: [] },
        dispositionIds,
        followUp: false,
        startHour: 0,
        endHour: 23,
      }
    );

    yield put(searchBorrowersSuccess(response.data));
  } catch (error: any) {
    yield put(
      searchBorrowersFailure(
        error.response?.data?.message || 'Failed to search borrowers'
      )
    );
  }
}

function* fetchDispositionSummarySaga(
  action: PayloadAction<{
    agentId: string;
    campaignId: string;
    limit?: number;
    offset?: number;
    taskType: TaskType;
    columns: Column[];
    dispositionIds: string[];
  }>
): Generator<any, void, any> {
  try {
    const {
      agentId,
      campaignId,
      limit = 10,
      offset = 0,
      taskType,
      columns,
      dispositionIds,
    } = action.payload;

    const response: AxiosResponse<DispositionSummaryResponse> = yield call(
      HTTPClient.PostRequest,
      supervisorCRMEndPoints.FETCH_DISPOSITION_WISE_SUMMARY(
        agentId,
        campaignId,
        limit,
        offset,
        taskType
      ),
      {
        filters: { columns },
        dispositionIds,
      }
    );

    // Transform response if needed
    const transformedResponse = {
      dispositions: response.data.dispositions || [],
    };

    yield put(fetchDispositionSummarySuccess(transformedResponse));
  } catch (error: any) {
    yield put(
      fetchDispositionSummaryFailure(
        error.response?.data?.message || 'Failed to fetch disposition summary'
      )
    );
  }
}

// Fetch campaigns by agent
function* fetchCampaignsByAgentSaga(
  action: PayloadAction<IFetchCampaignsByAgentRequest>
) {
  try {
    const { agentId } = action.payload;
    const response: AxiosResponse<IFetchCampaignsByAgentResponse[]> =
      yield HTTPClient.GetRequest(
        `${crmEndPoints.FETCH_CAMPAIGNS_BY_AGENT(agentId)}`
      );
    yield put(fetchCampaignsByAgentSuccess(response.data));
    const lastSelectedCampaign = response.data.filter(
      (campaign) => campaign.lastSelectedCampaign === true
    );
    if (lastSelectedCampaign.length <= 0) return;
    yield put(setCurrentCampaign(lastSelectedCampaign[0]));
    // message.success('Campaigns fetched successfully');
  } catch (error: any) {
    yield put(
      fetchCampaignsByAgentFailure(
        error.response?.data?.message || 'Failed to fetch disposition summary'
      )
    );
  }
}

function* saveCampaignSaga(action: PayloadAction<ISaveCampaignRequest>) {
  try {
    const { allocatorId, campaignId, taskType, count, isMissedCall } =
      action.payload;
    const response: AxiosResponse<ISaveCampaignResponse> =
      yield HTTPClient.PostRequest(
        `${crmEndPoints.SAVE_CAMPAIGN({
          taskType: taskType || TASK_TYPES.ALL_TASK,
          count: Number(count) || 0,
        })}`,
        {
          allocatorId,
          campaignId,
          isMissedCall,
        }
      );
    yield put(saveCampaignSuccess(response.data));
  } catch (error: any) {
    yield put(
      saveCampaignFailure(
        error.response?.data?.message || 'Failed to save campaign'
      )
    );
  }
}

export function* supervisorDashboardSaga() {
  yield takeLatest(fetchBorrowersRequest.type, fetchBorrowersSaga);
  yield takeLatest(searchBorrowersRequest.type, searchBorrowersSaga);
  yield takeLatest(
    fetchDispositionSummaryRequest.type,
    fetchDispositionSummarySaga
  );
  yield takeLatest(fetchCampaignsByAgent.type, fetchCampaignsByAgentSaga);
  yield takeLatest(saveCampaign.type, saveCampaignSaga);
}
